import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CHILE_GEO, REGIONES_LIST, getProvincias, getComunas } from "../lib/chile_geo";

/**
 * Cascading Region → Provincia → Comuna selector.
 *
 * Props:
 *  - value: { region, provincia, comuna }
 *  - onChange: (next) => void
 *  - showLabels: bool (default true)
 *  - testidPrefix: string used to build data-testid attributes
 */
export default function LocationSelector({ value = {}, onChange, showLabels = true, testidPrefix = "loc" }) {
  const region = value.region || "";
  const provincia = value.provincia || "";
  const comuna = value.comuna || "";

  const provincias = getProvincias(region);
  const comunas = getComunas(region, provincia);

  const update = (patch) => onChange({ ...value, ...patch });

  return (
    <>
      <div>
        {showLabels && <Label>Región</Label>}
        <Select
          value={region || "__none__"}
          onValueChange={(v) => update({ region: v === "__none__" ? "" : v, provincia: "", comuna: "" })}
        >
          <SelectTrigger data-testid={`${testidPrefix}-region`}>
            <SelectValue placeholder="Selecciona región" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">—</SelectItem>
            {REGIONES_LIST.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        {showLabels && <Label>Provincia</Label>}
        <Select
          value={provincia || "__none__"}
          onValueChange={(v) => update({ provincia: v === "__none__" ? "" : v, comuna: "" })}
          disabled={!region}
        >
          <SelectTrigger data-testid={`${testidPrefix}-provincia`}>
            <SelectValue placeholder={region ? "Selecciona provincia" : "Selecciona región primero"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">—</SelectItem>
            {provincias.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        {showLabels && <Label>Comuna</Label>}
        <Select
          value={comuna || "__none__"}
          onValueChange={(v) => update({ comuna: v === "__none__" ? "" : v })}
          disabled={!provincia}
        >
          <SelectTrigger data-testid={`${testidPrefix}-comuna`}>
            <SelectValue placeholder={provincia ? "Selecciona comuna" : "Selecciona provincia primero"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">—</SelectItem>
            {comunas.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

/**
 * Filter version: standalone Region + Comuna (no labels, with "Todas" option).
 * Used in list pages where we filter records by region/comuna.
 */
export function LocationFilter({ region, comuna, onChange, includeProvincia = false, provincia = "" }) {
  const provs = getProvincias(region);
  const allComunas = region
    ? Object.values(CHILE_GEO[region] || {}).flat()
    : [];
  const comunasList = includeProvincia && provincia ? getComunas(region, provincia) : allComunas;

  return (
    <>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Región</Label>
        <Select
          value={region || "__all__"}
          onValueChange={(v) => onChange({ region: v === "__all__" ? "" : v, provincia: "", comuna: "" })}
        >
          <SelectTrigger className="w-48" data-testid="filter-region"><SelectValue placeholder="Todas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            {REGIONES_LIST.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {includeProvincia && (
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Provincia</Label>
          <Select
            value={provincia || "__all__"}
            onValueChange={(v) => onChange({ region, provincia: v === "__all__" ? "" : v, comuna: "" })}
            disabled={!region}
          >
            <SelectTrigger className="w-48" data-testid="filter-provincia"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas</SelectItem>
              {provs.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Comuna</Label>
        <Select
          value={comuna || "__all__"}
          onValueChange={(v) => onChange({ region, provincia, comuna: v === "__all__" ? "" : v })}
          disabled={!region}
        >
          <SelectTrigger className="w-48" data-testid="filter-comuna"><SelectValue placeholder="Todas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            {comunasList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
